import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {Message, MessageService} from 'primeng/api';
import {Toast} from 'primeng/toast';
import {EventEnum} from '@app/enums/event.enum';
import {
    NotificacionWeb,
    NotificacionWebSocket,
    TipoNotificacionWeb,
    TipoNotificacionWebOption
} from '@app/domain/notificacion-web';
import {NotificacionWebService} from '@app/service/notificacion-web.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {NotificacionWebActionService} from '@app/service/notificacion-web-action.service';
import {BrowserNotificationService} from '@app/service/browser-notification.service';
import {ListasService} from '@app/service/listas.service';

@Component({
    selector: 'app-notification-indicator',
    templateUrl: './notification-indicator.component.html',
    styleUrls: ['./notification-indicator.component.scss']
})
export class NotificationIndicatorComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('recentPanel') recentPanel: OverlayPanel;
    @ViewChild('floatingToast') floatingToast: Toast;

    readonly recentPageSize = 10;
    readonly historyPageSize = 10;
    readonly requireUnreadNotificationToExecute = true;
    readonly floatingNotificationToastKey = 'web-notifications';
    readonly recentNotificationTypes: Array<{codigo?: TipoNotificacionWeb; nombre: string}> = [
        {nombre: 'Todos'},
        {codigo: 'PEDIDO_CATALOGO', nombre: 'Catálogo'},
        {codigo: 'PEDIDO_MENU', nombre: 'Menú'},
        {codigo: 'PAGO_YAPE', nombre: 'Pagos'}
    ];
    private readonly generalNotificationSoundUrl = 'assets/resources/sounds/notificacion-general.mp3';
    private readonly yapeNotificationSoundUrl = 'assets/resources/sounds/notificacion-yape.mp3';
    private readonly floatingNotificationLife = 5000;

    recentNotifications: NotificacionWeb[] = [];
    historyNotifications: NotificacionWeb[] = [];
    historyTotal = 0;
    historyPage = 0;
    historyVisible = false;
    historyNotificationType: TipoNotificacionWeb;
    historyNotificationTypes: TipoNotificacionWebOption[] = [];
    historyDateFrom: Date;
    historyDateTo: Date;
    historySoloNoAtendidas = false;
    loadingRecent = false;
    loadingRecentMore = false;
    recentHasMore = false;
    recentPage = 0;
    loadingHistory = false;
    novedadesCount = 0;
    recentNotificationType: TipoNotificacionWeb;

    private readonly destroy$ = new Subject<void>();
    private readonly openingNotificationIds = new Set<number>();
    private floatingToastElement: HTMLElement;

    constructor(private notificationService: NotificacionWebService,
                private socketIoService: SocketIoService,
                private notificationActionService: NotificacionWebActionService,
                private messageService: MessageService,
                public browserNotifications: BrowserNotificationService,
                public listas: ListasService) {
    }

    ngOnInit(): void {
        this.refreshNovedades();
        this.loadNotificationTypes();
        this.socketIoService.onEvent(EventEnum.NOTIFICATION)
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => this.handleSocketNotification(event));
    }

    ngAfterViewInit(): void {
        this.floatingToastElement = this.floatingToast?.containerViewChild?.nativeElement;
        if (this.floatingToastElement) {
            document.body.appendChild(this.floatingToastElement);
        }
    }

    openRecent(event: Event, panel: OverlayPanel): void {
        panel.toggle(event);
    }

    onRecentPanelShow(): void {
        this.recentNotificationType = undefined;
        this.loadRecent();
        this.novedadesCount = 0;
        this.notificationService.marcarVistas().subscribe({
            error: () => {
                // Best-effort: si falla, el proximo count lo volvera a mostrar.
            }
        });
    }

    limpiarNotificaciones(): void {
        this.recentNotifications = [];
        this.recentHasMore = false;
        this.recentPage = 0;
        this.novedadesCount = 0;
        this.notificationService.limpiarRecientes().subscribe({
            error: () => this.loadRecent()
        });
    }

    selectRecentNotificationType(tipo?: TipoNotificacionWeb): void {
        if (this.recentNotificationType === tipo) {
            return;
        }
        this.recentNotificationType = tipo;
        this.loadRecent();
    }

    openHistory(panel: OverlayPanel): void {
        panel.hide();
        this.resetHistoryFilters();
        this.historyVisible = true;
        this.loadHistory(0);
    }

    onHistoryPageChange(event: {page?: number}): void {
        this.loadHistory(event.page || 0);
    }

    onHistoryNotificationTypeChange(): void {
        this.loadHistory(0);
    }

    clearHistoryFilters(): void {
        this.resetHistoryFilters();
        this.loadHistory(0);
    }

    onHistorySoloNoAtendidasChange(): void {
        this.loadHistory(0);
    }

    onHistoryDateChange(): void {
        if (!this.isValidDate(this.historyDateFrom) || !this.isValidDate(this.historyDateTo)) {
            return;
        }
        if (this.historyDateFrom > this.historyDateTo) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Filtro de fechas',
                detail: 'La fecha desde no puede ser posterior a la fecha hasta.'
            });
            return;
        }
        this.loadHistory(0);
    }

    notificationIcon(code: TipoNotificacionWeb): string {
        switch (code) {
            case 'PEDIDO_CATALOGO':
                return 'pi pi-shopping-cart';
            case 'PEDIDO_MENU':
                return 'pi pi-book';
            case 'PAGO_YAPE':
                return 'pi pi-wallet';
            default:
                return 'pi pi-bell';
        }
    }

    notificationShortTitle(notification: NotificacionWeb): string {
        return notification.titulo || 'Nueva notificación';
    }

    trackByNotificationId(index: number, notification: NotificacionWeb): number {
        return notification.id;
    }

    get badgeText(): string {
        return this.novedadesCount > 99 ? '+99' : this.novedadesCount.toString();
    }

    markAsOpened(notification: NotificacionWeb): void {
        if (this.openingNotificationIds.has(notification.id)) {
            return;
        }
        if (notification.abierto) {
            if (this.requireUnreadNotificationToExecute && !this.canReopenNotification(notification)) {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Notificación',
                    detail: 'Esta notificación ya fue abierta.'
                });
                return;
            }
            this.notificationActionService.execute(notification, () => this.closeNotificationPanels());
            return;
        }

        this.updateNotificationState(notification.id, true);
        this.openingNotificationIds.add(notification.id);
        this.notificationService.markAsOpened(notification.id)
            .pipe(finalize(() => this.openingNotificationIds.delete(notification.id)))
            .subscribe({
                next: () => {
                    this.notificationActionService.execute(notification, () => this.closeNotificationPanels());
                },
                error: () => {
                    this.updateNotificationState(notification.id, false);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Notificación',
                        detail: 'No se pudo marcar la notificación como abierta.'
                    });
                }
            });
    }

    openFloatingNotification(message: Message): void {
        const notification = message?.data as NotificacionWeb;
        if (!notification) {
            return;
        }
        const messageIndex = this.floatingToast?.messages?.indexOf(message) ?? -1;
        if (messageIndex >= 0) {
            this.floatingToast.onMessageClose({message, index: messageIndex});
        }
        this.markAsOpened(notification);
    }

    ngOnDestroy(): void {
        this.floatingToastElement?.remove();
        this.destroy$.next();
        this.destroy$.complete();
    }

    onRecentScroll(event: Event): void {
        if (!this.recentHasMore || this.loadingRecent || this.loadingRecentMore) {
            return;
        }
        const element = event.target as HTMLElement;
        if (element.scrollTop + element.clientHeight >= element.scrollHeight - 40) {
            this.loadMoreRecent();
        }
    }

    private loadRecent(): void {
        this.loadingRecent = true;
        this.recentPage = 0;
        this.notificationService.findAll(
            0,
            this.recentPageSize,
            this.recentNotificationType
        ).subscribe({
            next: page => {
                this.recentNotifications = page.content || [];
                this.recentHasMore = !page.last;
                this.loadingRecent = false;
            },
            error: () => this.loadingRecent = false
        });
    }

    private loadMoreRecent(): void {
        this.loadingRecentMore = true;
        const nextPage = this.recentPage + 1;
        this.notificationService.findAll(
            nextPage,
            this.recentPageSize,
            this.recentNotificationType
        ).subscribe({
            next: page => {
                const existentes = new Set(this.recentNotifications.map(item => item.id));
                const nuevas = (page.content || []).filter(item => !existentes.has(item.id));
                this.recentNotifications = [...this.recentNotifications, ...nuevas];
                this.recentPage = page.number != null ? page.number : nextPage;
                this.recentHasMore = !page.last;
                this.loadingRecentMore = false;
            },
            error: () => this.loadingRecentMore = false
        });
    }

    private canReopenNotification(notification: NotificacionWeb): boolean {
        return notification.codigo === 'PEDIDO_MENU'
            || notification.codigo === 'PEDIDO_CATALOGO'
            || notification.codigo === 'PAGO_YAPE';
    }

    private loadNotificationTypes(): void {
        this.notificationService.findTypes().subscribe({
            next: types => this.historyNotificationTypes = types || []
        });
    }

    private loadHistory(pageNumber: number): void {
        const hasCompleteDateRange = this.isValidDate(this.historyDateFrom)
            && this.isValidDate(this.historyDateTo);
        this.loadingHistory = true;
        this.notificationService.findHistory(
            pageNumber,
            this.historyPageSize,
            this.historyNotificationType,
            hasCompleteDateRange ? this.historyDateFrom : undefined,
            hasCompleteDateRange ? this.historyDateTo : undefined,
            this.historySoloNoAtendidas
        ).subscribe({
            next: page => {
                this.historyNotifications = page.content || [];
                this.historyTotal = page.totalElements || 0;
                this.historyPage = page.number || 0;
                this.loadingHistory = false;
            },
            error: () => this.loadingHistory = false
        });
    }

    private isValidDate(date: Date): boolean {
        return date instanceof Date && !isNaN(date.getTime());
    }

    private resetHistoryFilters(): void {
        this.historyNotificationType = undefined;
        this.historyDateFrom = undefined;
        this.historyDateTo = undefined;
        this.historySoloNoAtendidas = false;
    }

    private updateNotificationState(id: number, abierto: boolean): void {
        [...this.recentNotifications, ...this.historyNotifications]
            .filter(notification => notification.id === id)
            .forEach(notification => {
                notification.abierto = abierto;
            });
    }

    private handleSocketNotification(event: any): void {
        const notification = (event?.value || event) as NotificacionWebSocket;
        if (!notification) {
            return;
        }
        if (notification.idEmpresa == null || !notification.codigo) {
            return;
        }
        if (notification.nueva === false) {
            this.loadRecent();
            if (this.historyVisible) {
                this.loadHistory(this.historyPage);
            }
            return;
        }
        this.playNotificationSound(notification.codigo);
        this.showFloatingNotification(notification);
        this.showBrowserNotification(notification);
        this.refreshNovedades();
    }

    enableBrowserNotifications(): void {
        this.browserNotifications.requestPermission().then(permission => {
            if (permission === 'granted') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Notificaciones',
                    detail: 'Las alertas del navegador quedaron activadas.'
                });
            } else if (permission === 'denied') {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Notificaciones',
                    detail: 'El navegador bloqueó las alertas. Puedes habilitarlas desde la configuración del sitio.'
                });
            }
        });
    }

    private showBrowserNotification(notification: NotificacionWebSocket): void {
        if (document.visibilityState === 'visible' && document.hasFocus()) {
            return;
        }
        this.browserNotifications.show({
            title: notification.titulo || this.defaultBrowserNotificationTitle(notification.codigo),
            body: notification.mensaje || 'Tienes una nueva notificación en Teki.',
            tag: notification.idRelacionado
                ? `teki-${notification.codigo.toLowerCase()}-${notification.idRelacionado}`
                : undefined,
            onClick: () => this.openBrowserNotificationTarget(notification)
        });
    }

    private defaultBrowserNotificationTitle(code: TipoNotificacionWeb): string {
        switch (code) {
            case 'PEDIDO_CATALOGO':
                return 'Pedido de catálogo';
            case 'PEDIDO_MENU':
                return 'Pedido de menú';
            case 'PAGO_YAPE':
                return 'Pago Yape';
            default:
                return 'Nueva notificación';
        }
    }

    private openBrowserNotificationTarget(notification: NotificacionWebSocket): void {
        const actionableNotification = this.toActionableNotification(notification);
        if (actionableNotification) {
            this.markAsOpened(actionableNotification);
            return;
        }
        if (notification.codigo === 'PAGO_YAPE') {
            this.notificationActionService.openPagosYape(() => this.closeNotificationPanels());
            return;
        }
        if (!notification.idRelacionado) {
            return;
        }
        this.notificationActionService.openPedidoById(notification.idRelacionado, () => this.closeNotificationPanels());
    }

    private showFloatingNotification(notification: NotificacionWebSocket): void {
        const actionableNotification = this.toActionableNotification(notification);
        if (!actionableNotification) {
            return;
        }
        this.messageService.add({
            key: this.floatingNotificationToastKey,
            summary: actionableNotification.id.toString(),
            detail: actionableNotification.mensaje,
            life: this.floatingNotificationLife,
            closable: true,
            data: actionableNotification
        });
    }

    private toActionableNotification(notification: NotificacionWebSocket): NotificacionWeb | null {
        const id = Number(notification.id);
        if (!Number.isSafeInteger(id) || id <= 0) {
            return null;
        }
        return {
            id,
            idRelacionado: notification.idRelacionado,
            codigo: notification.codigo,
            titulo: notification.titulo || this.defaultBrowserNotificationTitle(notification.codigo),
            mensaje: notification.mensaje || 'Tienes una nueva notificación en Teki.',
            payload: notification.payload,
            abierto: false,
            createdOn: notification.createdOn || new Date()
        };
    }

    private playNotificationSound(tipo: TipoNotificacionWeb): void {
        const soundUrl = tipo === 'PAGO_YAPE'
            ? this.yapeNotificationSoundUrl
            : this.generalNotificationSoundUrl;
        const audio = new Audio(soundUrl);
        audio.play().catch(() => {
            // El navegador puede bloquear audio hasta que exista interaccion del usuario.
        });
    }

    private closeNotificationPanels(): void {
        if (this.recentPanel) {
            this.recentPanel.hide();
        }
        this.historyVisible = false;
    }

    private refreshNovedades(): void {
        this.notificationService.countNovedades().subscribe({
            next: response => {
                this.novedadesCount = response.novedades || 0;
            }
        });
    }
}
